/**
 * Relationship resolver — decide which panels are open after the user
 * requests to open `candidateId`.
 *
 * Returns the new logical openPanels list ([{ id, props, config }, ...]).
 * Incompatible panels are dropped; measured stage visibility is handled
 * separately. Invalid candidates leave the input unchanged.
 */

import type {
    OpenPanel, PanelAdmits, PanelConfig, PanelId, PanelMode, PanelRegistry,
} from '@/types/panels';

interface ResolveOpenOpts {
    openPanels: OpenPanel[];
    candidateId: string;
    candidateProps?: Record<string, unknown> | null;
    mode: PanelMode;
    registry: PanelRegistry;
}

export function resolveOpen({
    openPanels, candidateId, candidateProps, mode, registry,
}: ResolveOpenOpts): OpenPanel[] {
    const candidate = registry[candidateId];
    if (!candidate) {
        console.warn(`[panels] tried to open unknown panel "${candidateId}"`);
        return openPanels;
    }

    // 1. UI mode gate (view vs settings)
    if (!candidate.config.modes.includes(mode)) {
        console.warn(`[panels] panel "${candidateId}" not allowed in mode "${mode}"`);
        return openPanels;
    }

    // If already open, just update props and bring to focus (caller handles focus).
    const existingOpenPanel = openPanels.find((panel) => panel.id === candidateId);
    if (existingOpenPanel) {
        return openPanels.map((panel) => (
            panel.id === candidateId
                ? { ...panel, props: { ...panel.props, ...(candidateProps ?? {}) } }
                : panel
        ));
    }

    // 2. Coexistence — drop panels incompatible with the candidate, then add it.
    // The excludes list is materialised by the registry from exclusions.ts
    // (default-deny: every panel not sharing a compatibility group).
    const workingOpenPanels = [
        ...openPanels.filter((panel) => !candidate.config.excludes.includes(panel.id)),
        { id: candidate.config.id, props: candidateProps ?? {}, config: candidate.config },
    ];

    // Width pressure never mutates logical open state. The measured stage owns
    // visibility and restores right-side panels when space returns.
    return workingOpenPanels;
}

/**
 * The auto-child a panel declares for `mode`. `config.autoChild` is either a
 * panel id (both modes) or a per-mode map `{ view, settings }` — see
 * definePanel.ts.
 */
export function getAutoChildId(
    config: PanelConfig | undefined, mode: PanelMode,
): PanelId | null {
    const spec = config?.autoChild;
    if (!spec) {
        return null;
    }
    return typeof spec === 'string' ? spec : (spec[mode] ?? null);
}

/**
 * True when some open panel other than the list itself sits visually to the
 * RIGHT of it (higher `config.order`).
 */
export function hasPanelRightOf(
    panels: { id: string; config: PanelConfig }[],
    listConfig: PanelConfig,
    listId: string,
): boolean {
    return panels.some(
        (panel) => panel.id !== listId && panel.config.order > listConfig.order);
}

interface AutoChildOpts {
    panels: OpenPanel[];
    candidateId: string;
    mode: PanelMode;
    registry: PanelRegistry;
    /** The stage's veto; omitted, every gate below is the whole rule. */
    admits?: PanelAdmits | null;
}

/** One step of the walk: the panels after `parentId`'s child, and that child. */
function appendAutoChild(
    { panels, candidateId: parentId, mode, registry, admits }: AutoChildOpts,
): { panels: OpenPanel[]; childId: PanelId | null } {
    const unchangedValue = { panels, childId: null };
    const listConfig = registry[parentId]?.config;
    const childId = getAutoChildId(listConfig, mode);
    if (!childId || !registry[childId] || !listConfig) {
        return unchangedValue;
    }
    if (panels.some((panel) => panel.id === childId)) {
        return unchangedValue;
    }
    if (hasPanelRightOf(panels, listConfig, parentId)) {
        return unchangedValue;
    }
    const withChild = resolveOpen({
        openPanels: panels,
        candidateId: childId,
        mode,
        registry,
    });
    const child = withChild.find((panel) => panel.id === childId);
    // Mode gate no-opped (child absent), or a panel was dropped — skip.
    if (!child) {
        return unchangedValue;
    }
    const remainingPanelIds = new Set(withChild.map((panel) => panel.id));
    if (!panels.every((panel) => remainingPanelIds.has(panel.id))) {
        return unchangedValue;
    }
    if (admits && !admits(panels, child)) {
        return unchangedValue;
    }
    return { panels: withChild, childId };
}

/**
 * Conditionally append `candidateId`'s declared auto-child so a list panel
 * and its detail surface open in ONE commit — a later panel-driven open
 * would resize the freshly-opened list (and every remaining panel) a second time.
 *
 * Unlike the auto-open ids the expansion is conditional. The child is added only
 * when ALL hold:
 *
 * 1. **The list ends up the rightmost open panel.** If anything — a video
 *    surface, an objects/events feed, the notifications drawer — still
 *    sits to the list's right after the list's own open resolved, the
 *    user has content on stage: just show the list. Panels the list's own
 *    exclusions closed don't count — the user navigated away from them,
 *    and without the child the stage would sit half empty (e.g. opening
 *    Users from the camera-settings flow).
 * 2. **No displacement.** Every already-open panel survives resolveOpen
 *    with the child as candidate — the auto-opened child must not remove a
 *    panel through an exclusion (e.g. a list swapping out the video surface).
 *    A user click may displace; an auto-open must not.
 * 3. **Room for it beside the list** (`admits`). An auto-open the stage has
 *    to hide, or could only show in the list's place, is not the offer the
 *    rule is making: the user asked for the list.
 *
 * A child may declare a child of its own, and the walk follows it under the
 * same gates — the cameras list opens the settings form, which opens the
 * preview beside it, in one commit.
 *
 * Returns the expanded list (auto-open ids NOT appended — the caller's
 * appendAutoOpenIds pass handles those), or `panels` unchanged when there is
 * no child, it's unknown / mode- or api-gated / already open, or a gate fails.
 */
export function resolveAutoChild(opts: AutoChildOpts): OpenPanel[] {
    let { panels, candidateId } = opts;
    for (;;) {
        const step = appendAutoChild({ ...opts, panels, candidateId });
        if (step.childId === null) {
            return panels;
        }
        panels = step.panels;
        candidateId = step.childId;
    }
}

/**
 * Hydrate raw openPanel records (id + props) with their config from the
 * registry. Drops any panels whose id is no longer in the registry.
 */
export function hydrate(
    rawOpenPanels: { id: string; props: Record<string, unknown> }[],
    registry: PanelRegistry,
): OpenPanel[] {
    const outOpenPanels: OpenPanel[] = [];
    for (const rawValue of rawOpenPanels) {
        const entry = registry[rawValue.id];
        if (!entry) {
            continue;
        }
        outOpenPanels.push({ ...rawValue, id: entry.config.id, config: entry.config });
    }
    return outOpenPanels;
}
